import { ColorFormData, ColorValidationErrors, COLOR_CONSTANTS } from '../types/color';
import { colorUtils } from './colorUtils';

/**
 * Validation utilities for color forms and data
 */
export const colorValidation = {
  /**
   * Validate a single field
   */
  validateField: (field: keyof ColorFormData, value: any): string | undefined => {
    switch (field) {
      case 'name':
        return validateColorName(value);
      case 'hexadecimal':
        return validateHexadecimal(value);
      case 'cmyk':
        return validateCmyk(value);
      case 'rgb':
        return validateRgb(value);
      default:
        return undefined;
    }
  },

  /**
   * Validate entire color form data
   */
  validateColorForm: (data: ColorFormData): ColorValidationErrors => {
    const errors: ColorValidationErrors = {};

    // Validate each field
    Object.keys(data).forEach((key) => {
      const field = key as keyof ColorFormData;
      const error = colorValidation.validateField(field, data[field]);
      if (error) {
        (errors as any)[field] = error;
      }
    });

    // Cross-field validation
    if (data.hexadecimal && data.rgb) {
      const hexRgb = colorUtils.hexToRgb(data.hexadecimal);
      const inputRgb = colorUtils.parseRgb(data.rgb);
      
      if (hexRgb && inputRgb) {
        const tolerance = 2; // Allow small differences due to rounding
        if (
          Math.abs(hexRgb.r - inputRgb.r) > tolerance ||
          Math.abs(hexRgb.g - inputRgb.g) > tolerance ||
          Math.abs(hexRgb.b - inputRgb.b) > tolerance
        ) {
          errors.rgb = 'RGB values do not match the hexadecimal color';
        }
      }
    }

    if (data.hexadecimal && data.cmyk) {
      const hexRgb = colorUtils.hexToRgb(data.hexadecimal);
      const inputCmyk = colorUtils.parseCmyk(data.cmyk);
      
      if (hexRgb && inputCmyk) {
        const calculatedCmyk = colorUtils.rgbToCmyk(hexRgb.r, hexRgb.g, hexRgb.b);
        const tolerance = 5; // Allow larger tolerance for CMYK due to conversion differences
        
        if (
          Math.abs(calculatedCmyk.c - inputCmyk.c) > tolerance ||
          Math.abs(calculatedCmyk.m - inputCmyk.m) > tolerance ||
          Math.abs(calculatedCmyk.y - inputCmyk.y) > tolerance ||
          Math.abs(calculatedCmyk.k - inputCmyk.k) > tolerance
        ) {
          errors.cmyk = 'CMYK values do not match the hexadecimal color';
        }
      }
    }

    return errors;
  },

  /**
   * Check if color form is valid
   */
  isColorFormValid: (data: ColorFormData): boolean => {
    const errors = colorValidation.validateColorForm(data);
    return Object.keys(errors).length === 0;
  },

  /**
   * Get validation summary
   */
  getValidationSummary: (data: ColorFormData): {
    isValid: boolean;
    errors: ColorValidationErrors;
    errorCount: number;
    fieldErrors: string[];
  } => {
    const errors = colorValidation.validateColorForm(data);
    const errorCount = Object.keys(errors).length;
    const fieldErrors = Object.entries(errors)
      .filter(([, error]) => error)
      .map(([field, error]) => `${field}: ${error}`);

    return {
      isValid: errorCount === 0,
      errors,
      errorCount,
      fieldErrors,
    };
  },
};

/**
 * Validate color name
 */
function validateColorName(name: any): string | undefined {
  if (!name || typeof name !== 'string') {
    return 'Color name is required';
  }

  const trimmedName = name.trim();
  
  if (trimmedName.length === 0) {
    return 'Color name cannot be empty';
  }

  if (trimmedName.length > COLOR_CONSTANTS.MAX_NAME_LENGTH) {
    return `Color name must be less than ${COLOR_CONSTANTS.MAX_NAME_LENGTH} characters`;
  }

  // Check for invalid characters (basic validation)
  if (!/^[a-zA-Z0-9\s\-_()]+$/.test(trimmedName)) {
    return 'Color name contains invalid characters';
  }

  return undefined;
}

/**
 * Validate hexadecimal color
 */
function validateHexadecimal(hex: any): string | undefined {
  if (!hex || typeof hex !== 'string') {
    return 'Hexadecimal color is required';
  }

  if (!colorUtils.isValidHex(hex)) {
    return 'Invalid hexadecimal color format (e.g., #FF0000 or #F00)';
  }

  return undefined;
}

/**
 * Validate RGB color string
 */
function validateRgb(rgb: any): string | undefined {
  if (!rgb) {
    return undefined; // RGB is optional
  }

  if (typeof rgb !== 'string') {
    return 'RGB must be a string';
  }

  if (!colorUtils.isValidRgb(rgb)) {
    return 'Invalid RGB format (e.g., "255, 0, 0" or "255,0,0")';
  }

  const parsedRgb = colorUtils.parseRgb(rgb);
  if (!parsedRgb) {
    return 'Could not parse RGB values';
  }

  // Additional validation for RGB ranges
  if (parsedRgb.r < 0 || parsedRgb.r > 255) {
    return 'Red value must be between 0 and 255';
  }
  if (parsedRgb.g < 0 || parsedRgb.g > 255) {
    return 'Green value must be between 0 and 255';
  }
  if (parsedRgb.b < 0 || parsedRgb.b > 255) {
    return 'Blue value must be between 0 and 255';
  }

  return undefined;
}

/**
 * Validate CMYK color string
 */
function validateCmyk(cmyk: any): string | undefined {
  if (!cmyk) {
    return undefined; // CMYK is optional
  }

  if (typeof cmyk !== 'string') {
    return 'CMYK must be a string';
  }

  if (!colorUtils.isValidCmyk(cmyk)) {
    return 'Invalid CMYK format (e.g., "0, 100, 100, 0" or "0,100,100,0")';
  }

  const parsedCmyk = colorUtils.parseCmyk(cmyk);
  if (!parsedCmyk) {
    return 'Could not parse CMYK values';
  }

  // Additional validation for CMYK ranges
  if (parsedCmyk.c < 0 || parsedCmyk.c > 100) {
    return 'Cyan value must be between 0 and 100';
  }
  if (parsedCmyk.m < 0 || parsedCmyk.m > 100) {
    return 'Magenta value must be between 0 and 100';
  }
  if (parsedCmyk.y < 0 || parsedCmyk.y > 100) {
    return 'Yellow value must be between 0 and 100';
  }
  if (parsedCmyk.k < 0 || parsedCmyk.k > 100) {
    return 'Black value must be between 0 and 100';
  }

  return undefined;
}

/**
 * Color accessibility utilities
 */
export const colorAccessibility = {
  /**
   * Check if color has sufficient contrast for text
   */
  hasGoodContrast: (backgroundColor: string, textColor: string = '#000000'): boolean => {
    const bgLuminance = colorUtils.getLuminance(backgroundColor);
    const textLuminance = colorUtils.getLuminance(textColor);
    
    const contrast = (Math.max(bgLuminance, textLuminance) + 0.05) / 
                    (Math.min(bgLuminance, textLuminance) + 0.05);
    
    return contrast >= 4.5; // WCAG AA standard
  },

  /**
   * Get contrast ratio between two colors
   */
  getContrastRatio: (color1: string, color2: string): number => {
    const lum1 = colorUtils.getLuminance(color1);
    const lum2 = colorUtils.getLuminance(color2);
    
    return (Math.max(lum1, lum2) + 0.05) / (Math.min(lum1, lum2) + 0.05);
  },

  /**
   * Suggest accessible text color for background
   */
  getAccessibleTextColor: (backgroundColor: string): string => {
    const whiteContrast = colorAccessibility.getContrastRatio(backgroundColor, '#FFFFFF');
    const blackContrast = colorAccessibility.getContrastRatio(backgroundColor, '#000000');
    
    return whiteContrast > blackContrast ? '#FFFFFF' : '#000000';
  },

  /**
   * Check if color is suitable for colorblind users
   */
  isColorblindFriendly: (color: string): {
    protanopia: boolean;
    deuteranopia: boolean;
    tritanopia: boolean;
    overall: boolean;
  } => {
    const rgb = colorUtils.hexToRgb(color);
    if (!rgb) {
      return { protanopia: false, deuteranopia: false, tritanopia: false, overall: false };
    }

    // Simplified colorblind simulation (basic check)
    const { r, g, b } = rgb;
    
    // Check for sufficient difference in color channels
    const protanopia = Math.abs(g - b) > 50; // Red-blind
    const deuteranopia = Math.abs(r - b) > 50; // Green-blind
    const tritanopia = Math.abs(r - g) > 50; // Blue-blind
    
    return {
      protanopia,
      deuteranopia,
      tritanopia,
      overall: protanopia && deuteranopia && tritanopia,
    };
  },
};
